import Vue from 'vue';
import VueRouter from 'vue-router';
import type { RouteConfig } from 'vue-router';

import StandardLayout from '@/layouts/standard-layout.vue';
import NotFound from '@/views/not-found.vue';
import { isPromise } from '@/shared/util';
import homeBackgroundImage from '@/assets/img/home/bg-header.png';

import agreementRoutes from './routes/agreement';
import verificationRoutes from './routes/verification';
import assessmentRoutes from './routes/assessment';
import bankcardRoutes from './routes/bankcard';
import creditLimitRoutes from './routes/creditLimit';
import informationRoutes from './routes/information';
import recordRoutes from './routes/record';
import repaymentRoutes from './routes/repayment';
import activityRoutes from './routes/activity';
import rulesRoutes from './routes/rules';
import partnerRoutes from './routes/partner';
import walletRoutes from './routes/wallet';
import loanRoutes from './routes/loan';
import pointsRoutes from './routes/points';
import vipRoutes from './routes/vip';
import orderRoutes from './routes/order';
import goodsRoutes from './routes/goods';
import supplementaryRoutes from './routes/supplementary';
import channelRoutes from './routes/channel';
import promotionRoutes from './routes/promotion';
import miscRoutes from './routes/misc';
import changeMobileRoutes from './routes/changeMobile';
import invoiceRoutes from './routes/invoice';
import testerRoutes from './routes/tester';
import autoRepay from './routes/autoRepay';
import vasRoutes from './routes/vas';
import customerService from './routes/customerService';

Vue.use(VueRouter);

/**
 * 路由入口，将所有的路由配置放在此处
 */
const routes: RouteConfig[] = [
  /**
   * 提供给APP的预加载的页面
   */
  {
    path: '/preload',
    meta: {
      excludeLogin: true,
    },
    component: Vue.extend({
      render(h) {
        return h();
      },
    }),
  },
  /**
   * 包含通用导航的页面
   */
  {
    path: '/',
    component: StandardLayout,
    meta: {
      hasNavBarLayout: true,
    },
    children: [
      {
        path: '',
        redirect: '/home',
      },
      // 开发测试环境的一些调试页面
      ...testerRoutes,
      // 授信认证
      ...verificationRoutes,
      // 协议
      ...agreementRoutes,
      // 授信结果
      ...assessmentRoutes,
      // 银行卡
      ...bankcardRoutes,
      // 额度管理
      ...creditLimitRoutes,
      // 个人信息
      ...informationRoutes,
      // 借款/还款记录
      ...recordRoutes,
      // 借款
      ...loanRoutes,
      // 还款
      ...repaymentRoutes,
      // 规则介绍
      ...rulesRoutes,
      // 活动
      ...activityRoutes,
      // API导流
      ...partnerRoutes,
      // 钱包
      ...walletRoutes,
      //变更手机号
      ...changeMobileRoutes,
      // 积分
      ...pointsRoutes,
      // 会员权益
      ...vipRoutes,
      // 订单
      ...orderRoutes,
      // 商品
      ...goodsRoutes,
      //补签协议
      ...supplementaryRoutes,
      // 发票中心
      ...invoiceRoutes,
      // 自动还款服务
      ...autoRepay,
      // 中收
      ...vasRoutes,
      //客服IM
      ...customerService,
    ],
  },
  /**
   * 首页
   */
  {
    path: '/home',
    meta: {
      background: `#f8f9ff url(${homeBackgroundImage}) no-repeat 0 0/100% auto`,
    },
    component: () => import('@/views/home/index.vue'),
  },
  /**
   * 默认登录页
   */
  {
    path: '/login',
    meta: {
      excludeLogin: true,
      background: '#fff',
    },
    component: () => import('@/views/login.vue'),
  },
  // 异业渠道
  ...channelRoutes,
  // 投放
  ...promotionRoutes,
  // 其他
  ...miscRoutes,
  /**
   * 404
   */
  {
    path: '*',
    name: 'NotFound',
    component: NotFound,
  },
  /**
   * APP首页C类客群
   * @deprecated
   */
  {
    path: '/home/customer/c',
    meta: {
      background: '#F5F5F5',
      excludeTracking: true,
    },
    component: () => import('@/views/home/customer-c/index.vue'),
  },
];

export function createRouter() {
  const router = new VueRouter({
    mode: 'history',
    base: process.env.VUE_APP_PREFIX,
    routes,
    scrollBehavior() {
      return { y: 0, x: 0 };
    },
  });

  const originalPush = router.push;
  router.push = function push(...args: any) {
    const result = originalPush.apply(this, args);

    if (isPromise(result)) {
      return (result as any).catch((err: Error) => err);
    }
  };

  const originalReplace = router.replace;
  router.replace = function replace(...args: any) {
    const result = originalReplace.apply(this, args);

    if (isPromise(result)) {
      return (result as any).catch((err: Error) => err);
    }
  };

  return router;
}
